/**
 * Delivery Estimate components for Product Page and Cart Drawer
 * – Reads saved postcode from localStorage (shared with delivery-location.js)
 * – Listens for delivery-location:changed event from the shared modal
 * – PDP: shows location + weight-based item count messaging
 * – Cart: shows live rate + per-item cost + item headroom
 */

// Free shipping postcode ranges (inclusive). O(log n) lookup via binary search.
//
// [May 5, 2026 - mid-promo tightening]
// Reduced eligibility to Capital + Outer Melbourne metros only based on
// AusPost cost data (Near State Metro / Far State Metro / Country zones
// were eating margin at $13-22/parcel vs $7-9 for Mel metro).
//
// Eligible zones (per AusPost area definitions):
//   - Sydney (Capital, NSW)
//   - Melbourne (Capital, VIC)
//   - Outer Melbourne / VIC Metro
//   - Brisbane (Capital, QLD)
//   - Adelaide (Capital, SA)
// Excluded: NSW Metro, VIC Country, QLD Metro, QLD Country, SA Metro,
// SA Country, Perth (Capital + Metro), WA Country, Hobart Metro, TAS
// Country, NT, all remote.
const FREE_SHIPPING_POSTCODE_RANGES = [
  // Sydney (Capital, NSW)
  [1000,1935],[2000,2079],[2085,2107],[2109,2156],[2158,2158],
  [2160,2172],[2174,2229],[2232,2249],[2557,2559],[2564,2567],
  [2740,2744],[2747,2751],[2759,2764],[2766,2774],[2776,2777],
  [2890,2897],
  // Melbourne + Outer Melbourne (VIC)
  [3000,3220],[3335,3341],[3350,3350],[3353,3356],[3427,3443],
  [3689,3690],[3750,3811],[3910,3920],[3926,3944],[3975,3978],
  [3980,3983],
  // Brisbane (Capital, QLD)
  [4000,4018],[4029,4068],[4072,4123],[4127,4129],[4131,4132],
  [4151,4164],[4169,4182],[4205,4206],
  // Adelaide (Capital, SA)
  [5000,5113],[5115,5117],[5125,5130],[5158,5169],[5800,5999],
  // PO Box / Reply Paid (VIC + QLD capitals)
  [8000,9725],[9999,9999]
];

function isFreeShippingPostcode(postcode) {
  const n = parseInt(postcode, 10);
  if (!n || isNaN(n)) return false;
  // Binary search the sorted ranges
  let lo = 0, hi = FREE_SHIPPING_POSTCODE_RANGES.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const [s, e] = FREE_SHIPPING_POSTCODE_RANGES[mid];
    if (n < s) hi = mid - 1;
    else if (n > e) lo = mid + 1;
    else return true;
  }
  return false;
}

// Shared utilities
const DeliveryUtils = {
  STORAGE_KEY: 'ap:delivery_location',
  SHIPPING_SHARED_KEY: 'ap:shipping_calculator:shared_v1',
  FREE_SHIPPING_THRESHOLD: 60,
  MAX_SHIPPING_WEIGHT_G: 5000,
  isFreeShippingPostcode: isFreeShippingPostcode,

  // Weight tier boundaries in grams - merged across all carriers:
  // Carrier 1 (Couriers Please): 300g, 500g, 1kg, 3kg, 5kg
  // AusPost eParcel: 500g, 1kg, 3kg, 5kg
  // Direct Freight: continuous per-kg (flat zone ceiling varies by zone - handled separately)
  WEIGHT_TIERS: [300, 500, 1000, 3000, 5000],

  // Packaging weight estimate based on product weight (in grams)
  estimatePackaging(productWeightG) {
    if (productWeightG <= 500) return 200;
    if (productWeightG <= 2000) return 270;
    if (productWeightG <= 5000) return 500;
    if (productWeightG <= 10000) return 700;
    return 1000;
  },

  // Get total shipping weight (product + packaging)
  shippingWeight(cartWeightG) {
    return cartWeightG + this.estimatePackaging(cartWeightG);
  },

  // Cache shipping rate data to build price tier map
  RATE_CACHE_KEY: 'ap:shipping_rates_cache',
  cacheRate(postcode, weightG, price) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.RATE_CACHE_KEY) || '{}');
      if (!cache[postcode]) cache[postcode] = [];

      // Add or update entry for this weight
      const existing = cache[postcode].findIndex(r => r.weight === weightG);
      if (existing >= 0) {
        cache[postcode][existing].price = price;
      } else {
        cache[postcode].push({ weight: weightG, price });
      }

      localStorage.setItem(this.RATE_CACHE_KEY, JSON.stringify(cache));
    } catch (e) { }
  },

  // Cache variant weight data to avoid repeated API calls
  VARIANT_CACHE_KEY: 'ap:variant_weights_cache',
  cacheVariantWeight(variantId, weight) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.VARIANT_CACHE_KEY) || '{}');
      cache[variantId] = { weight, cached_at: Date.now() };
      localStorage.setItem(this.VARIANT_CACHE_KEY, JSON.stringify(cache));
    } catch (e) { }
  },

  getVariantWeightFromCache(variantId) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.VARIANT_CACHE_KEY) || '{}');
      return cache[variantId] ? cache[variantId].weight : null;
    } catch (e) {
      return null;
    }
  },

  async fetchVariantWeights(variantIds) {
    const weights = {};

    // Check cache first
    for (const id of variantIds) {
      const cached = this.getVariantWeightFromCache(id);
      if (cached !== null) {
        weights[id] = cached;
      }
    }

    // Check injected data from Liquid template
    const injectedWeights = window.cartVariantWeights || {};
    for (const id of variantIds) {
      if (!weights[id] && injectedWeights[id] !== undefined) {
        weights[id] = injectedWeights[id];
        this.cacheVariantWeight(id, injectedWeights[id]);
      }
    }

    // Fill in any missing weights with 0 (fallback)
    for (const id of variantIds) {
      if (weights[id] === undefined) {
        weights[id] = 0;
      }
    }

    return weights;
  },

  // Get next price increase from cache
  getNextPriceIncrease(postcode, currentWeightG, currentPrice) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.RATE_CACHE_KEY) || '{}');
      const rates = (cache[postcode] || []).sort((a, b) => a.weight - b.weight);

      // Find next weight where price increases (look at weights BEYOND current)
      for (const rate of rates) {
        if (rate.weight > currentWeightG && parseFloat(rate.price) > currentPrice) {
          return { weight: rate.weight, price: rate.price };
        }
      }

      // Also check if we have multiple data points (indicating cache is active)
      // and suggest weight progression instead of misleading tier info
      if (rates.length >= 2) {
        return 'cache_active'; // Signal that cache exists but no price jump yet
      }
    } catch (e) { }
    return null;
  },

  // Find the current tier ceiling and how much headroom remains
  tierHeadroom(cartWeightG) {
    const totalWeight = this.shippingWeight(cartWeightG);

    for (const tier of this.WEIGHT_TIERS) {
      if (totalWeight <= tier) {
        return {
          currentTier: tier,
          headroomG: Math.max(0, tier - totalWeight),
          totalWeight: totalWeight,
        };
      }
    }

    // Over 5kg - per-kg pricing, next "tier" is the next whole kg
    const nextKg = Math.ceil(totalWeight / 1000) * 1000;
    return {
      currentTier: nextKg,
      headroomG: Math.max(0, nextKg - totalWeight),
      totalWeight: totalWeight,
    };
  },

  // Get weight progress toward next tier (returns percentage and absolute values)
  getWeightProgress(cartWeightG) {
    const info = this.tierHeadroom(cartWeightG);
    const percentUsed = (info.totalWeight / info.currentTier) * 100;

    return {
      current: info.totalWeight,
      ceiling: info.currentTier,
      headroom: info.headroomG,
      percentUsed: Math.min(100, percentUsed), // Cap at 100%
    };
  },

  stateFromPostcode(pc) {
    const n = parseInt(pc, 10);
    if (n === 2899) return { country: 'Norfolk Island', province: '', short: 'NF' };
    if (n >= 2600 && n <= 2618) return { country: 'Australia', province: 'Australian Capital Territory', short: 'ACT' };
    if (n >= 2900 && n <= 2920) return { country: 'Australia', province: 'Australian Capital Territory', short: 'ACT' };
    if (n >= 800 && n <= 999) return { country: 'Australia', province: 'Northern Territory', short: 'NT' };
    if (n >= 1000 && n <= 2599) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
    if (n >= 2619 && n <= 2898) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
    if (n >= 2900 && n <= 2999) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
    if (n >= 3000 && n <= 3999) return { country: 'Australia', province: 'Victoria', short: 'VIC' };
    if (n >= 8000 && n <= 8999) return { country: 'Australia', province: 'Victoria', short: 'VIC' };
    if (n >= 4000 && n <= 4999) return { country: 'Australia', province: 'Queensland', short: 'QLD' };
    if (n >= 9000 && n <= 9999) return { country: 'Australia', province: 'Queensland', short: 'QLD' };
    if (n >= 5000 && n <= 5999) return { country: 'Australia', province: 'South Australia', short: 'SA' };
    if (n >= 6000 && n <= 6797) return { country: 'Australia', province: 'Western Australia', short: 'WA' };
    if (n === 6798) return { country: 'Christmas Island', province: '', short: 'CX' };
    if (n === 6799) return { country: 'Cocos (Keeling) Islands', province: '', short: 'CC' };
    if (n >= 6800 && n <= 6999) return { country: 'Australia', province: 'Western Australia', short: 'WA' };
    if (n >= 7000 && n <= 7999) return { country: 'Australia', province: 'Tasmania', short: 'TAS' };
    return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
  },

  readLocation() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  },

  getLocationLabel(stored) {
    if (!stored || !stored.postcode) return null;
    const suburb = stored.suburb || this.stateFromPostcode(stored.postcode).short;
    return `${suburb} ${stored.postcode}`;
  },
};


/**
 * Product Page - Delivery Estimate
 */
if (!customElements.get('delivery-estimate-pdp')) {
  class DeliveryEstimatePDP extends HTMLElement {
    connectedCallback() {
      this.locationEl = this.querySelector('.js-pdp-location');
      this.shippingMsg = this.querySelector('.js-pdp-shipping-msg');
      this.shippingInfo = this.querySelector('.js-pdp-shipping-info');
      this.productWeight = parseFloat(this.dataset.productWeight) || 0;

      this.restore();

      // Listen for location changes from the shared modal
      document.addEventListener('delivery-location:changed', (e) => {
        this.applyLocation(e.detail);
      });

      // Listen for variant changes to update the weight-based messaging
      document.addEventListener('on:variant:change', (e) => {
        const variant = e.detail && e.detail.variant;
        if (variant && variant.weight != null) {
          this.productWeight = variant.weight;
          const stored = DeliveryUtils.readLocation();
          if (stored) this.applyLocation(stored);
        }
      });
    }

    restore() {
      const stored = DeliveryUtils.readLocation();
      if (stored && stored.postcode) {
        this.applyLocation(stored);
      }
    }

    applyLocation(stored) {
      const label = DeliveryUtils.getLocationLabel(stored);
      if (label && this.locationEl) {
        this.locationEl.textContent = label;
      }

      // [May 2026] Postcode-aware shipping copy. If the customer's saved
      // postcode is eligible for free delivery, surface the threshold ($60,
      // under 5kg). Otherwise keep the weight-based copy. Tooltip swaps too
      // so excluded postcodes don't see misleading "FREE delivery" info.
      if (stored && stored.postcode) {
        const eligible = DeliveryUtils.isFreeShippingPostcode(stored.postcode);
        if (this.shippingMsg) {
          if (eligible) {
            this.shippingMsg.innerHTML = '<strong style="color:#017e5b;">FREE delivery</strong> on orders over $60 (under 5kg)';
          } else {
            this.shippingMsg.textContent = 'Shipping is weight-based - add more items, pay less per item!';
          }
        }
        if (this.shippingInfo) {
          if (eligible) {
            this.shippingInfo.textContent = 'FREE delivery on orders over $60 (under 5kg) for most metro and regional postcodes. Some remote areas are excluded - your actual rate is calculated at checkout based on weight and location.';
          } else {
            // [May 2026] No mention of free delivery for non-eligible postcodes -
            // calling out that they "can't have" it reads as exclusionary and
            // creates FOMO at a conversion-critical moment. Lead with the
            // positive (bundling saves money) instead.
            this.shippingInfo.textContent = 'Shipping is calculated by weight and location - the more you add, the less each item costs to ship.';
          }
        }
      }
    }
  }
  customElements.define('delivery-estimate-pdp', DeliveryEstimatePDP);
}


/**
 * Cart Drawer - Delivery Estimate
 */
if (!customElements.get('delivery-estimate-cart')) {
  class DeliveryEstimateCart extends HTMLElement {
    connectedCallback() {
      this.locationEl = this.querySelector('.js-cart-location');
      this.rateContainer = this.querySelector('.js-cart-rate');
      this.rateText = this.querySelector('.js-cart-rate-text');
      this.perItemEl = this.querySelector('.js-cart-per-item');
      this.headroomEl = this.querySelector('.js-cart-headroom');
      this.loadingEl = this.querySelector('.js-cart-loading');
      this.milestoneEl = this.querySelector('.js-cart-milestone');
      this.milestoneMsg = this.querySelector('.js-milestone-msg');
      this.milestoneFill = this.querySelector('.js-milestone-fill');
      this.milestoneProgress = this.querySelector('.js-milestone-progress');
      this.weightValueEl = this.querySelector('.js-weight-value');
      this.weightFillEl = this.querySelector('.js-weight-fill');
      this.overweightNoteEl = this.querySelector('.js-overweight-note');
      this.rateFooterEl = this.querySelector('.js-rate-footer');
      this.rateFooterPriceEl = this.querySelector('.js-rate-footer-price');
      this.moreLabelEl = this.querySelector('.js-more-label');
      this.moreTextEl = this.querySelector('.js-more-text');

      // Read config from data attributes (overridable from Liquid)
      this.freeThreshold = parseFloat(this.dataset.freeThreshold) || DeliveryUtils.FREE_SHIPPING_THRESHOLD;
      this.maxWeightG = parseInt(this.dataset.maxWeightG, 10) || DeliveryUtils.MAX_SHIPPING_WEIGHT_G;
      this.packagingG = parseInt(this.dataset.packagingG, 10) || 500;

      this.restore();

      // Store bound handlers so disconnectedCallback can remove them
      this._onLocationChanged = (e) => this.fetchAndDisplay(e.detail.postcode);
      this._onCartUpdated = () => this.refreshCart();

      document.addEventListener('delivery-location:changed', this._onLocationChanged);
      document.addEventListener('cart:updated', this._onCartUpdated);
      document.addEventListener('cart:change', this._onCartUpdated);
    }

    disconnectedCallback() {
      document.removeEventListener('delivery-location:changed', this._onLocationChanged);
      document.removeEventListener('cart:updated', this._onCartUpdated);
      document.removeEventListener('cart:change', this._onCartUpdated);
    }

    restore() {
      const stored = DeliveryUtils.readLocation();
      if (stored && stored.postcode) {
        const label = DeliveryUtils.getLocationLabel(stored);
        if (this.locationEl) this.locationEl.textContent = label;
        this.fetchAndDisplay(stored.postcode);
      }
    }

    async refreshCart() {
      const stored = DeliveryUtils.readLocation();
      if (stored && stored.postcode) {
        this.fetchAndDisplay(stored.postcode);
      }
    }

    async fetchAndDisplay(postcode) {
      const state = DeliveryUtils.stateFromPostcode(postcode);
      const stored = DeliveryUtils.readLocation();
      const label = DeliveryUtils.getLocationLabel(stored || { postcode, suburb: state.short });
      if (this.locationEl) this.locationEl.textContent = label;

      let cart;
      try {
        const res = await fetch('/cart.js');
        cart = await res.json();
      } catch (e) { return; }

      if (!cart.item_count) {
        this.hideAllViews();
        this.loadingEl.hidden = true;
        return;
      }

      // [May 2026] Decide which view to render:
      //   1. Postcode in free-shipping zone + total shipping weight <= 5kg
      //      → milestone bar ($XX away from $60 free) + weight bar
      //   2. Postcode in free-shipping zone + over 5kg
      //      → hard "over 5kg" message + live rate fallback
      //   3. Postcode NOT in free-shipping zone
      //      → live rate (existing behaviour)
      const productWeightG = cart.total_weight;
      const totalShippingWeightG = productWeightG + this.packagingG;
      const isEligible = DeliveryUtils.isFreeShippingPostcode(postcode);
      const isOverWeight = totalShippingWeightG > this.maxWeightG;
      const subtotal = cart.total_price / 100; // total_price is in cents

      if (isEligible) {
        // Always render the milestone container for eligible postcodes - the
        // internal pieces (subtotal bar, weight bar color, overweight note,
        // top message) toggle by state. Keeping the container constant
        // avoids layout swaps when crossing thresholds mid-cart.
        const remaining = Math.max(0, this.freeThreshold - subtotal);
        const unlocked = remaining <= 0;
        // Fetch the live shipping rate when:
        //   - Over 5kg (need it as the top message)
        //   - Under threshold + under weight (show as "Or pay shipping today")
        //     so customer can decide between adding more for free vs paying now
        // Skip when unlocked (free delivery active, no need to surface rate).
        let rate = null;
        if (isOverWeight || !unlocked) {
          this.loadingEl.hidden = false;
          rate = await this.fetchRate(postcode, state, cart);
          this.loadingEl.hidden = true;
        }
        this.renderMilestoneView(subtotal, productWeightG, totalShippingWeightG, isOverWeight, rate);
        this.rateContainer.hidden = true;
        this.loadingEl.hidden = true;
        return;
      }

      // Postcode not eligible - hide milestone, show live rate as before.
      this.milestoneEl.hidden = true;
      await this.renderLiveRateView(postcode, state, cart, false, false);
    }

    async fetchRate(postcode, state, cart) {
      const variantIds = cart.items.map(item => item.variant_id);
      await DeliveryUtils.fetchVariantWeights(variantIds);

      const params = new URLSearchParams({
        'shipping_address[zip]': postcode,
        'shipping_address[country]': state.country,
        'shipping_address[province]': state.province,
      });

      try {
        const res = await fetch(`/cart/shipping_rates.json?${params}`);
        if (!res.ok) throw new Error('Rate fetch failed');
        const data = await res.json();
        if (data.shipping_rates && data.shipping_rates.length > 0) {
          const cheapest = data.shipping_rates.reduce((min, r) =>
            parseFloat(r.price) < parseFloat(min.price) ? r : min
          );
          const currentPrice = parseFloat(cheapest.price);
          const perItemText = (currentPrice > 0 && cart.item_count > 0)
            ? `($${(currentPrice / cart.item_count).toFixed(2)}/item)`
            : '';
          return { currentPrice, perItemText };
        }
      } catch (e) { /* silent */ }
      return null;
    }

    hideAllViews() {
      if (this.milestoneEl) this.milestoneEl.hidden = true;
      if (this.rateContainer) this.rateContainer.hidden = true;
    }

    renderMilestoneView(subtotal, productWeightG, totalShippingWeightG, isOverWeight, rate) {
      const threshold = this.freeThreshold;
      const remaining = Math.max(0, threshold - subtotal);
      const unlocked = remaining <= 0;

      // Reset state classes - applied below per state
      this.milestoneEl.classList.remove('is-unlocked', 'is-overweight');

      // Top message + subtotal bar/progress text
      if (isOverWeight) {
        // Over 5kg → free delivery doesn't apply on this order. Surface the
        // actual shipping price as the top message instead of the unlock
        // celebration. Bar stays green/full so the layout doesn't reflow.
        this.milestoneEl.classList.add('is-overweight');
        if (rate && rate.currentPrice != null) {
          const priceText = rate.currentPrice === 0
            ? 'Free shipping!'
            : `Shipping: $${rate.currentPrice.toFixed(2)}`;
          const perItem = rate.perItemText
            ? ` <span class="dec__per-item-inline">${rate.perItemText}</span>`
            : '';
          this.milestoneMsg.innerHTML = priceText + perItem;
        } else {
          this.milestoneMsg.textContent = 'Calculating shipping…';
        }
        this.milestoneFill.style.width = '100%';
        this.milestoneProgress.hidden = true;
      } else if (unlocked) {
        this.milestoneEl.classList.add('is-unlocked');
        this.milestoneMsg.innerHTML = '🎉 You\'ve unlocked FREE delivery!';
        this.milestoneFill.style.width = '100%';
        // No price text when unlocked - just the celebration
        this.milestoneProgress.hidden = true;
      } else {
        this.milestoneMsg.innerHTML = `<span class="dec__amt">$${remaining.toFixed(2)}</span> away from FREE delivery`;
        this.milestoneProgress.textContent = `$${subtotal.toFixed(2)} / $${threshold.toFixed(0)}`;
        this.milestoneProgress.hidden = false;
        const subPct = Math.min(100, (subtotal / threshold) * 100);
        this.milestoneFill.style.width = `${subPct}%`;
      }

      // Show "Or pay shipping today: $X.XX" footer when not unlocked + not
      // overweight, so the customer can compare paying vs spending more.
      if (this.rateFooterEl) {
        if (!unlocked && !isOverWeight && rate && rate.currentPrice != null) {
          const priceTxt = rate.currentPrice === 0
            ? 'Free'
            : `$${rate.currentPrice.toFixed(2)}`;
          this.rateFooterPriceEl.textContent = priceTxt;
          this.rateFooterEl.hidden = false;
        } else {
          this.rateFooterEl.hidden = true;
        }
      }

      // Weight row - always show packaging-inclusive weight against 5kg max
      const weightLabel = totalShippingWeightG < 1000
        ? `${Math.round(totalShippingWeightG)}g`
        : `${(totalShippingWeightG / 1000).toFixed(2)}kg`;
      const maxLabel = `${(this.maxWeightG / 1000).toFixed(0)}kg`;
      this.weightValueEl.textContent = `${weightLabel} / ${maxLabel} (incl. packaging)`;

      const weightPct = Math.min(100, (totalShippingWeightG / this.maxWeightG) * 100);
      this.weightFillEl.style.width = `${weightPct}%`;
      // Green when under 5kg, red when over - keeps the visual stable for
      // the entire under-5kg range and only flips at the actual limit.
      this.weightFillEl.classList.toggle('is-overweight', !!isOverWeight);

      // Inline note under the weight bar. When over the 5kg limit, surface
      // an *actionable* recovery message ("Remove Xg to unlock free delivery")
      // instead of just stating the problem - turns a dead end into a play.
      if (this.overweightNoteEl) {
        if (isOverWeight) {
          const excessG = totalShippingWeightG - this.maxWeightG;
          // Round up to nearest 10g so we never tell the customer to remove
          // less than they actually need.
          const excessRounded = Math.ceil(excessG / 10) * 10;
          const excessLabel = excessRounded < 1000
            ? `${excessRounded}g`
            : `${(excessRounded / 1000).toFixed(2)}kg`;
          this.overweightNoteEl.innerHTML =
            `<span aria-hidden="true">💡</span> Reduce by ${excessLabel} to unlock FREE delivery`;
          this.overweightNoteEl.hidden = false;
        } else {
          this.overweightNoteEl.hidden = true;
        }
      }

      this.milestoneEl.hidden = false;

      // Swap the "why we charge" copy for free-shipping context. Tweak by state:
      //   unlocked → "Delivery details" (FREE already secured, less prominent)
      //   over 5kg → "Why no free delivery?" (lean into the question)
      //   under threshold → "About free delivery" (informational)
      if (this.moreLabelEl) {
        if (unlocked) {
          this.moreLabelEl.textContent = 'Delivery details';
        } else if (isOverWeight) {
          this.moreLabelEl.textContent = 'Why no free delivery?';
        } else {
          this.moreLabelEl.textContent = 'About free delivery';
        }
      }
      if (this.moreTextEl) {
        if (isOverWeight) {
          this.moreTextEl.textContent = 'Free delivery applies to orders up to 5kg total weight (including packaging) for eligible postcodes. This order exceeds the weight limit.';
        } else if (unlocked) {
          this.moreTextEl.textContent = 'Your order qualifies for FREE delivery. Eligibility: $' + threshold.toFixed(0) + '+ for eligible postcodes, up to 5kg including packaging.';
        } else {
          this.moreTextEl.textContent = 'Free delivery on orders over $' + threshold.toFixed(0) +
            ' for eligible postcodes (orders up to 5kg total weight including packaging).';
        }
      }
    }

    async renderLiveRateView(postcode, state, cart, showPackagingInclusive, hideWeightBar) {
      this.loadingEl.hidden = false;
      this.rateContainer.hidden = true;

      // [May 2026] Reset expander copy for the live-rate state. This matters
      // when a customer changes from an eligible postcode (FREE delivery copy)
      // to a non-eligible one mid-session - we don't want the old eligible
      // copy lingering. Skip reset when eligible-but-overweight, since
      // renderMilestoneView is also rendering and sets its own copy.
      const eligible = DeliveryUtils.isFreeShippingPostcode(postcode);
      if (this.moreLabelEl && this.moreTextEl && !eligible) {
        this.moreLabelEl.textContent = 'Shipping info';
        this.moreTextEl.textContent = 'Shipping is calculated by weight and location - the more you add, the less each item costs to ship.';
      }

      // Fetch variant weights before proceeding with calculations
      const variantIds = cart.items.map(item => item.variant_id);
      const variantWeights = await DeliveryUtils.fetchVariantWeights(variantIds);

      const itemsWithWeights = cart.items.map(item => ({
        ...item,
        weight: variantWeights[item.variant_id] || 0
      }));

      const params = new URLSearchParams({
        'shipping_address[zip]': postcode,
        'shipping_address[country]': state.country,
        'shipping_address[province]': state.province,
      });

      try {
        const res = await fetch(`/cart/shipping_rates.json?${params}`);
        if (!res.ok) throw new Error('Rate fetch failed');
        const data = await res.json();

        if (data.shipping_rates && data.shipping_rates.length > 0) {
          const cheapest = data.shipping_rates.reduce((min, r) =>
            parseFloat(r.price) < parseFloat(min.price) ? r : min
          );
          const currentPrice = parseFloat(cheapest.price);

          const productWeightG = cart.total_weight;
          // When eligible-but-overweight, surface the packaging-inclusive weight
          // so shoppers see why their 4.8kg cart was disqualified (4.8 + 0.5 = 5.3).
          const displayWeightG = showPackagingInclusive
            ? productWeightG + this.packagingG
            : productWeightG;
          const tierInfo = DeliveryUtils.tierHeadroom(productWeightG);

          this.rateText.textContent = currentPrice === 0 ? 'Free shipping!' : `Shipping: $${currentPrice.toFixed(2)}`;

          if (currentPrice > 0 && cart.item_count > 0) {
            this.perItemEl.textContent = `($${(currentPrice / cart.item_count).toFixed(2)}/item)`;
          } else {
            this.perItemEl.textContent = '';
          }
          if (hideWeightBar) {
            // Suppress the live-rate weight bar when the milestone view is
            // also visible - the milestone already shows packaging-inclusive
            // weight against 5kg, so duplicating it here just adds noise.
            this.headroomEl.hidden = true;
          } else if (tierInfo.headroomG > 0) {
            this.renderWeightProgressBar(displayWeightG, tierInfo.currentTier, tierInfo.headroomG, currentPrice, itemsWithWeights, showPackagingInclusive);
          } else {
            this.headroomEl.hidden = true;
          }

          this.rateContainer.hidden = false;
        }
      } catch (err) {
        // Silent fail
      }

      this.loadingEl.hidden = true;
    }

    renderPriceProgressBar(currentWeightG, nextPriceWeightG, currentPrice, nextPrice, headroomG, items) {
      const percentUsed = (currentWeightG / nextPriceWeightG) * 100;

      this.headroomEl.innerHTML = `
        <div class="cart-progress-bar">
          <div class="cart-progress-fill" style="width: ${Math.min(100, percentUsed)}%"></div>
        </div>
        <p class="cart-progress-label">Next tier: $${parseFloat(nextPrice).toFixed(2)}</p>
      `;
      this.headroomEl.hidden = false;
    }

    renderWeightProgressBar(currentWeightG, tierCeilingG, headroomG, currentPrice, items, showPackagingInclusive) {
      // Show on first load before we have price data
      const percentUsed = (currentWeightG / tierCeilingG) * 100;

      const currentLabel = currentWeightG < 1000
        ? `${Math.round(currentWeightG)}g`
        : `${(currentWeightG / 1000).toFixed(1)}kg`;

      const suffix = showPackagingInclusive ? ' (incl. packaging)' : '';

      this.headroomEl.innerHTML = `
        <div class="cart-progress-bar">
          <div class="cart-progress-fill" style="width: ${percentUsed}%"></div>
        </div>
        <p class="cart-progress-label">${currentLabel}${suffix}</p>
      `;
      this.headroomEl.hidden = false;
    }

  }
  customElements.define('delivery-estimate-cart', DeliveryEstimateCart);
}
